import { createContext, useContext, useState } from 'react';
import { SnackBarWrapper } from '@echotex/business-modules';
import { GlobalModal, GlobalModalOuterContextProvider } from '../../components/GlobalModal';
import { PageTypes } from '@core-web/types';
import type { IPageState, IPageWrapperProvider, ISnackBarState } from './IPageWrapperContext';
import { ISnackBar } from '@core-web/ui/SnackBar';

const DummyMethod = () => {};

// the context for the snackbar
const SnackBarContext = createContext<ISnackBarState>({
  snackBar: {
    open: false,
    message: '',
    variant: 'success',
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    onClose: DummyMethod,
  },
  setSnackBar: DummyMethod,
});

SnackBarContext.displayName = 'SnackBarContext';

// the context for the page state
const PageStateContext = createContext<IPageState>({
  selectedItem: null,
  updateSelectedItem: DummyMethod,
  pageTitle: '',
  pageId: 0,
  isSave: false,
  isView: false,
  isDelete: false,
  isEdit: false,
  activePage: PageTypes.FORM,
  setActivePage: DummyMethod,
});

PageStateContext.displayName = 'DashboardPageStateContext';

// The hook to expose the context value
export const usePageState = () => {
  return useContext(PageStateContext);
};

// The hook to expose the snackbar context value
export const useSnackBar = () => {
  return useContext(SnackBarContext);
};

// The context provider
export const PageStateContextProvider = ({ children, ...rest }: IPageWrapperProvider): JSX.Element => {
  const [selectedItem, updateSelectedItem] = useState<any>(null);
  const [activePage, setActivePage] = useState<PageTypes>(PageTypes.FORM);
  const [snackBar, setSnackBar] = useState<ISnackBar>({
    open: false,
    message: '',
  });

  const handleSnackBarHandler = (_snackBar: Partial<ISnackBar>) => {
    setSnackBar({
      ...snackBar,
      open: true,
      variant: 'success',
      ..._snackBar,
    });
  };

  const handleCloseSnackBarHandler = () => {
    setSnackBar({
      ...snackBar,
      open: false,
    });
  };

  return (
    <PageStateContext.Provider
      value={{
        selectedItem,
        updateSelectedItem,
        activePage,
        setActivePage,
        ...rest,
      }}>
      <SnackBarContext.Provider
        value={{
          snackBar,
          setSnackBar: handleSnackBarHandler,
        }}>
        <GlobalModalOuterContextProvider>
          {children} <GlobalModal />
        </GlobalModalOuterContextProvider>
      </SnackBarContext.Provider>
      <SnackBarWrapper {...snackBar} onClose={handleCloseSnackBarHandler} />
    </PageStateContext.Provider>
  );
};
